import { useEffect, useRef } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isTokenExpired } from '../utils/tokenUtils';
import EnquiryOnlyWrapper from './EnquiryOnlyWrapper';

const ProtectedRoute = ({ pageRoute, children }) => {
    const { user, hasPermission, loading, logout } = useAuth();
    const logoutFired = useRef(false);

    // When a token is detected as expired during render, we cannot call
    // logout() synchronously (it triggers React state updates & navigation).
    // Instead we redirect immediately via <Navigate /> and let this effect
    // clean up auth state on the next micro-task.
    useEffect(() => {
        if (user?.token && isTokenExpired(user.token) && !logoutFired.current) {
            logoutFired.current = true;
            logout(true);
        }
    }, [user, logout]);

    if (loading) {
        return <div>Loading...</div>;
    }

    // Redirect to login if not authenticated
    if (!user) {
        return <Navigate to="/" replace />;
    }

    // Synchronously block rendering when the JWT has expired.
    // The useEffect above will call logout() to clear state and show a toast.
    if (isTokenExpired(user.token)) {
        return <Navigate to="/" replace />;
    }

    // Redirect to unauthorized page if user doesn't have permission
    if (!hasPermission(pageRoute)) {
        return <Navigate to="/unauthorized" replace />;
    }

    // Wrap content with EnquiryOnlyWrapper to enforce read-only mode
    // when the user has enquiry-only (read-only) access to this page route.
    // This disables all interactive elements and shows a read-only banner.
    const content = children ? children : <Outlet />;
    return <EnquiryOnlyWrapper pageRoute={pageRoute}>{content}</EnquiryOnlyWrapper>;
};

export default ProtectedRoute;